/* External dependencies */
import { Div, Text } from 'atomize';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

/* Local dependencies */
import { navigate } from 'gatsby';
import formatMessage from '../../../intl/formatMessage';
import { RootState } from '../../../redux/store';
import { resetConsigneeProfilesState } from '../../consignee-profiles/redux/actions';
import { CustomButton } from '../../ui/custom-button/custom-button';
import { CustomPopup } from '../../ui/popup/custom-popup';
import { Sidebar } from '../../ui/sidebar/create-consignee-good-specifications-sidebar';
import { MenuItemData } from '../../ui/sidebar/menu-item';
import { ConsigneesPath } from '../helper';
import { CreateConsigneeGoodSpecificationsControlBar } from './create-consignee-good-specifications-control-bar';
import { Table } from './create-consignee-good-specifications-table';
import {
  addColumnToSidebar,
  addColumnToTable,
  resetCreateConsigneeGoodsSpec,
  setCreateConsigneeGoodField,
} from './redux/actions';
import { Column, Specification } from './redux/types';

export const EMPTY = '__EMPTY';

export function CreateConsigneeGoodSpecifications() {
  const dispatch = useDispatch();

  const { sections, columns, files, error, groupedFileUrl } = useSelector(
    (state: RootState) => state.createConsigneeGoodsSpecification,
  );

  const memoizedColumns = useMemo(() => columns, [columns]);
  const memoizedSections = useMemo(() => sections, [sections]);

  useEffect(() => {
    return () => {
      dispatch(resetCreateConsigneeGoodsSpec());
      dispatch(resetConsigneeProfilesState());
    };
  }, []);

  const onAddColumnToTable = useCallback(
    (menuItemData: MenuItemData) => {
      const { parentName, name, index } = menuItemData;
      const fileData = files[parentName] || [];
      
      const id = `${parentName}-${name}-${Date.now()}`;
      const specification = Specification.DEFAULT;

      const columnValues = fileData.map((row) => row[name] || EMPTY);

      const preparedColumn: Column = {
        id,
        specification,
        fileName: parentName,
        columnName: name,
        position: index,
        values: columnValues,
      };

      dispatch(addColumnToTable(preparedColumn));
    },
    [files, dispatch],
  );

  function onAddColumnToSidebar(column: Column) {
    dispatch(addColumnToSidebar(column));
  }

  const onSelectColumnSpecification = useCallback(
    (column: Column) => {
      const { position, specification } = column;

      const updatedColumns = columns.map((col: Column, index: number) =>
        index === position ? { ...col, specification } : col,
      );

      dispatch(setCreateConsigneeGoodField('columns', updatedColumns));
    },
    [columns, dispatch],
  );

  function onNavigateToListConsigneeSpecifications() {
    navigate(ConsigneesPath.LIST);
  }

  function onClearError() {
    dispatch(setCreateConsigneeGoodField('error', null));
  }

  return (
    <Div w="100%">
      <Div
        d="flex"
        flexDir="column"
        m="0 auto"
        p={{ x: '1.5rem' }}
        maxW="1200px"
      >
        <CreateConsigneeGoodSpecificationsControlBar />

        <Div d="flex" h="72vh" overflow="hidden">
          <Sidebar
            title={formatMessage('files')}
            sections={memoizedSections}
            menuItemButtonStyle={addButtonStyle}
            onAdd={onAddColumnToTable}
          />

          <Table
            items={memoizedColumns}
            options={Object.values(Specification)}
            menuItemButtonStyle={removeButtonStyle}
            onSelectColumn={onAddColumnToSidebar}
            onSelectOption={onSelectColumnSpecification}
          />
        </Div>
      </Div>

      {groupedFileUrl && (
        <CustomPopup
          title={formatMessage('operationSucceeded')}
          submitText={formatMessage('close')}
          submitButtonProps={successPopupButtonStyle}
          onSubmit={onNavigateToListConsigneeSpecifications}
        >
          <Div d="flex" style={{ gap: '10px' }}>
            <Text>{formatMessage('downloadFileInstruction')}</Text>

            <a
              href={groupedFileUrl}
              download
              target="_blank"
              rel="noopener noreferrer"
            >
              <CustomButton {...downloadButtonStyle} />
            </a>
          </Div>
        </CustomPopup>
      )}

      {error && (
        <CustomPopup
          title={formatMessage('error')}
          submitText={formatMessage('close')}
          onSubmit={onClearError}
        >
          {formatMessage(error?.message || String(error))}
        </CustomPopup>
      )}
    </Div>
  );
}

const successPopupButtonStyle = {
  bg: 'primary_color',
  hoverBg: 'primary_hover_text_color',
};

const removeButtonStyle = {
  icon: { name: 'Minus' },
};

const addButtonStyle = {
  bg: 'success_color',
  hoverBg: 'success_hover_text_color',

  icon: { name: 'Plus' },
};

const downloadButtonStyle = {
  bg: 'success_color',
  hoverBg: 'success_hover_text_color',
  p: '3px',

  icon: { name: 'Download' },
};
